import { useRef } from 'react';

interface UseModalArgs {
  handlers: Partial<{ onClose: VoidFunction; onOpen: VoidFunction }>;
  stopInsideFocus: boolean;
}

const useModal = ({
  handlers,
  stopInsideFocus,
}: Partial<UseModalArgs> | undefined = {}) => {
  const ref = useRef<HTMLDialogElement>(null);
  const interactionStartedInsideModal = useRef(false);

  const openModal = () => {
    ref.current?.showModal();
    handlers?.onOpen?.();

    if (stopInsideFocus) {
      requestAnimationFrame(() => {
        if (ref.current) {
          ref.current.focus();
        }
      });
    }
  };

  const closeModal = () => {
    ref.current?.close();
    handlers?.onClose?.();
  };

  const trackInteractionStart = (
    e: React.MouseEvent<HTMLDialogElement> | React.TouchEvent<HTMLDialogElement>
  ) => {
    interactionStartedInsideModal.current = e.target !== ref.current;
  };

  const onClick = (e: React.SyntheticEvent<HTMLDialogElement>) => {
    const clickIsOnMask =
      !interactionStartedInsideModal.current && e.target === ref.current;

    const clickIsOnClosableElement =
      (e.target as HTMLElement).getAttribute('data-close-modal') !== null;

    if (clickIsOnMask || clickIsOnClosableElement) {
      closeModal();
    }
  };

  return {
    ref,
    openModal,
    closeModal,
    maskClickHandlers: {
      onMouseDown: trackInteractionStart,
      onTouchStart: trackInteractionStart,
      onClick,
    },
  };
};

export default useModal;
